import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {GET} from '../../../services/api'
import {Loading} from '../../../components/Loading/Loading'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
} from 'chart.js'
import {Bar, Line, Pie, Doughnut} from 'react-chartjs-2'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
)

type BookingDashboardMetrics = {
  totalBookings: number
  confirmedBookings: number
  cancelledBookings?: number
  noShowBookings?: number
  pendingBookings?: number
  averagePeoplePerBooking: number
  totalPeople: number
  conversionRate: number
  noShowRate: number
  averageBookingAdvance: number
  // Campos opcionais para futuras expansões
  bookingsByDay?: Array<{date: string; count: number}>
  bookingsByHour?: Array<{hour: string; count: number}>
  bookingsByEnvironment?: Array<{environment: string; count: number}>
  bookingsByStatus?: Array<{status: string; count: number}>
  topCustomers?: Array<{name: string; bookingsCount: number}>
  peakBookingDays?: Array<{day: string; count: number}>
  bookingTrends?: Array<{period: string; count: number; growth: number}>
  averageGroupSize?: number
  mostRequestedHours?: Array<{hour: string; count: number}>
}

const BookingManagementDashboardContent: FC = () => {
  const [loading, setLoading] = useState(false)
  const [metrics, setMetrics] = useState<BookingDashboardMetrics | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [period, setPeriod] = useState<'today' | 'week' | 'month' | 'year' | 'custom'>('month')
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [startHour, setStartHour] = useState<string>('')
  const [endHour, setEndHour] = useState<string>('')

  useEffect(() => {
    loadDashboardData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, startDate, endDate, startHour, endHour])

  const buildUrl = (periodValue: string, start?: string, end?: string, sHour?: string, eHour?: string): string => {
    let url = `/queueReservation/booking/dashboard?period=${periodValue}`
    
    // Adicionar filtros customizados se houver datas
    if (start || end || sHour || eHour) {
      if (start) url += `&startDate=${start}`
      if (end) url += `&endDate=${end}`
      // Só adiciona hora se AMBAS forem preenchidas
      if (sHour && eHour) {
        url += `&startHour=${sHour}`
        url += `&endHour=${eHour}`
      }
    }
    
    return url
  }

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      setError(null)

      const url = buildUrl(period, startDate, endDate, startHour, endHour)

      console.log('📡 Fazendo requisição para:', url)
      const res = await GET(url)

      console.log('📦 Resposta recebida:', res)

      if (res.success && res.data) {
        setMetrics(res.data)
        console.log('✅ Métricas carregadas com sucesso:', res.data)
      } else if (res.data) {
        // Se a resposta tiver data mas não tiver success flag
        setMetrics(res.data)
        console.log('✅ Métricas carregadas (sem flag success):', res.data)
      } else if (res.message) {
        console.warn('⚠️ Erro na resposta:', res.message)
        setError(res.message || 'Erro ao carregar dados')
        setMetrics(null)
      } else {
        console.warn('⚠️ Resposta sem dados:', res)
        setError('Nenhum dado disponível')
        setMetrics(null)
      }

      setLoading(false)
    } catch (e: any) {
      console.error('❌ Erro ao carregar dashboard:', e)
      const errorMsg = e.response?.data?.message || e.message || 'Erro ao carregar dashboard'
      setError(errorMsg)
      setMetrics(null)
      setLoading(false)
    }
  }

  const formatDays = (days: number) => {
    if (days < 1) return `${Math.round(days * 24)}h`
    return `${Math.round(days)} dia${days > 1 ? 's' : ''}`
  }

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  Dashboard - Gestão de Reservas
                </span>
                <BreadCrumbs />
              </h3>
            </div>

            {/* Seção de Filtros Destacados */}
            <div className='card-body bg-light-primary border-bottom border-primary border-2'>
              <h5 className='mb-4 fw-bold text-primary'>
                <i className='fas fa-filter me-2'></i>
                Filtrar por Período
              </h5>
              <div className='row g-3 align-items-end'>
                <div className='col-lg-2'>
                  <label className='form-label fw-bold'>Data Início</label>
                  <input
                    type='date'
                    className='form-control form-control-lg'
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className='col-lg-2'>
                  <label className='form-label fw-bold'>Data Fim</label>
                  <input
                    type='date'
                    className='form-control form-control-lg'
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
                <div className='col-lg-2'>
                  <label className='form-label fw-bold'>Hora Início</label>
                  <input
                    type='time'
                    className='form-control form-control-lg'
                    value={startHour}
                    onChange={(e) => setStartHour(e.target.value)}
                  />
                </div>
                <div className='col-lg-2'>
                  <label className='form-label fw-bold'>Hora Fim</label>
                  <input
                    type='time'
                    className='form-control form-control-lg'
                    value={endHour}
                    onChange={(e) => setEndHour(e.target.value)}
                  />
                </div>
                <div className='col-lg-4'>
                  <button
                    className='btn btn-primary btn-lg w-100 mb-2'
                    onClick={() => {
                      setPeriod('custom')
                    }}
                  >
                    <i className='fas fa-search me-2'></i>
                    Buscar com Filtros Personalizados
                  </button>
                  <button
                    className='btn btn-light btn-lg w-100'
                    onClick={() => {
                      setStartDate('')
                      setEndDate('')
                      setStartHour('')
                      setEndHour('')
                      setPeriod('month')
                    }}
                  >
                    <i className='fas fa-redo me-2'></i>
                    Limpar Filtros
                  </button>
                </div>
              </div>

              {/* Aviso informativo */}
              <div className='alert alert-info mt-3 mb-0'>
                <i className='fas fa-info-circle me-2'></i>
                <strong>Dica:</strong> Preencha apenas as datas para buscar em qualquer horário. 
                Se quiser filtrar por horário específico, preencha AMBAS as horas (início e fim).
              </div>
            </div>

            {/* Período Rápido */}
            <div className='card-body border-bottom'>
              <h5 className='mb-3 fw-bold'>
                <i className='fas fa-calendar-alt me-2'></i>
                Períodos Rápidos
              </h5>
              <div className='button-group d-flex gap-2 flex-wrap'>
                <button
                  className={`btn ${period === 'today' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => {
                    setStartDate('')
                    setEndDate('')
                    setStartHour('')
                    setEndHour('')
                    setPeriod('today')
                  }}
                >
                  Hoje
                </button>
                <button
                  className={`btn ${period === 'week' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => {
                    setStartDate('')
                    setEndDate('')
                    setStartHour('')
                    setEndHour('')
                    setPeriod('week')
                  }}
                >
                  Última Semana
                </button>
                <button
                  className={`btn ${period === 'month' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => {
                    setStartDate('')
                    setEndDate('')
                    setStartHour('')
                    setEndHour('')
                    setPeriod('month')
                  }}
                >
                  Último Mês
                </button>
                <button
                  className={`btn ${period === 'year' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => {
                    setStartDate('')
                    setEndDate('')
                    setStartHour('')
                    setEndHour('')
                    setPeriod('year')
                  }}
                >
                  Último Ano
                </button>
              </div>

              {/* Aviso quando há filtros customizados */}
              {(startDate || endDate || startHour || endHour) && (
                <div className='alert alert-warning mt-3 mb-0'>
                  <i className='fas fa-exclamation-triangle me-2'></i>
                  <strong>Filtros Customizados Ativos:</strong> Os períodos rápidos serão ignorados. 
                  Clique em "Limpar Filtros" acima ou em um período rápido para retornar aos períodos pré-definidos.
                </div>
              )}
            </div>

            <div className='card-body'>
              {error && (
                <div className='alert alert-danger d-flex align-items-center mb-4'>
                  <i className='fas fa-exclamation-circle me-3 fs-2'></i>
                  <div>
                    <h5 className='mb-1'>Erro ao Carregar Dados</h5>
                    <p className='mb-0'>{error}</p>
                  </div>
                </div>
              )}
              {metrics ? (
                <>
                  {/* Cards de Métricas Principais */}
                  <div className='row g-5 mb-8'>
                    <div className='col-md-3'>
                      <div className='card bg-light-primary'>
                        <div className='card-body'>
                          <div className='d-flex align-items-center'>
                            <i className='fas fa-calendar-check fs-2x text-primary me-4'></i>
                            <div>
                              <h3 className='fw-bolder text-primary mb-1'>
                                {metrics.totalBookings}
                              </h3>
                              <span className='text-muted fw-bold fs-7'>
                                Total de Reservas
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className='col-md-3'>
                      <div className='card bg-light-success'>
                        <div className='card-body'>
                          <div className='d-flex align-items-center'>
                            <i className='fas fa-check-double fs-2x text-success me-4'></i>
                            <div>
                              <h3 className='fw-bolder text-success mb-1'>
                                {metrics.confirmedBookings}
                              </h3>
                              <span className='text-muted fw-bold fs-7'>
                                Confirmadas
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className='col-md-3'>
                      <div className='card bg-light-warning'>
                        <div className='card-body'>
                          <div className='d-flex align-items-center'>
                            <i className='fas fa-users fs-2x text-warning me-4'></i>
                            <div>
                              <h3 className='fw-bolder text-warning mb-1'>
                                {metrics.totalPeople}
                              </h3>
                              <span className='text-muted fw-bold fs-7'>
                                Total de Pessoas
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className='col-md-3'>
                      <div className='card bg-light-info'>
                        <div className='card-body'>
                          <div className='d-flex align-items-center'>
                            <i className='fas fa-percentage fs-2x text-info me-4'></i>
                            <div>
                              <h3 className='fw-bolder text-info mb-1'>
                                {metrics.conversionRate.toFixed(1)}%
                              </h3>
                              <span className='text-muted fw-bold fs-7'>
                                Taxa de Conversão
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Cards Secundários */}
                  <div className='row g-5 mb-8'>
                    <div className='col-md-4'>
                      <div className='card border border-gray-300'>
                        <div className='card-body'>
                          <div className='d-flex justify-content-between align-items-center'>
                            <div>
                              <span className='text-muted fw-bold fs-7 d-block mb-2'>
                                Média de Pessoas/Reserva
                              </span>
                              <h4 className='fw-bolder mb-0'>
                                {metrics.averagePeoplePerBooking.toFixed(1)}
                              </h4>
                            </div>
                            <i className='fas fa-user-friends fs-2x text-primary'></i>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className='col-md-4'>
                      <div className='card border border-gray-300'>
                        <div className='card-body'>
                          <div className='d-flex justify-content-between align-items-center'>
                            <div>
                              <span className='text-muted fw-bold fs-7 d-block mb-2'>
                                Taxa de No-Show
                              </span>
                              <h4 className='fw-bolder mb-0'>
                                {metrics.noShowRate.toFixed(1)}%
                              </h4>
                            </div>
                            <i className='fas fa-user-times fs-2x text-danger'></i>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className='col-md-4'>
                      <div className='card border border-gray-300'>
                        <div className='card-body'>
                          <div className='d-flex justify-content-between align-items-center'>
                            <div>
                              <span className='text-muted fw-bold fs-7 d-block mb-2'>
                                Antecedência Média
                              </span>
                              <h4 className='fw-bolder mb-0'>
                                {formatDays(metrics.averageBookingAdvance)}
                              </h4>
                            </div>
                            <i className='fas fa-calendar-alt fs-2x text-info'></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Gráficos - Linha 1 */}
                  <div className='row g-5 mb-8'>
                    {metrics.bookingsByDay && metrics.bookingsByDay.length > 0 ? (
                      <div className='col-md-8'>
                        <div className='card'>
                          <div className='card-header'>
                            <h4 className='card-title'>Tendência de Reservas</h4>
                          </div>
                          <div className='card-body'>
                            <Line
                              data={{
                                labels: metrics.bookingsByDay.map((d) => d.date),
                                datasets: [
                                  {
                                    label: 'Reservas',
                                    data: metrics.bookingsByDay.map((d) => d.count),
                                    borderColor: 'rgb(75, 192, 192)',
                                    backgroundColor: 'rgba(75, 192, 192, 0.2)',
                                    tension: 0.4,
                                    fill: true,
                                  },
                                ],
                              }}
                              options={{
                                responsive: true,
                                plugins: {
                                  legend: {
                                    position: 'top' as const,
                                  },
                                },
                                scales: {
                                  y: {
                                    beginAtZero: true,
                                  },
                                },
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className='col-md-8'>
                        <div className='card'>
                          <div className='card-header'>
                            <h4 className='card-title'>Tendência de Reservas</h4>
                          </div>
                          <div className='card-body'>
                            <p className='text-muted'>Dados não disponíveis</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {metrics.bookingsByStatus && metrics.bookingsByStatus.length > 0 ? (
                      <div className='col-md-4'>
                        <div className='card'>
                          <div className='card-header'>
                            <h4 className='card-title'>Status das Reservas</h4>
                          </div>
                          <div className='card-body'>
                            <Doughnut
                              data={{
                                labels: metrics.bookingsByStatus.map((s) => s.status),
                                datasets: [
                                  {
                                    label: 'Quantidade',
                                    data: metrics.bookingsByStatus.map((s) => s.count),
                                    backgroundColor: [
                                      'rgba(75, 192, 192, 0.6)',
                                      'rgba(255, 206, 86, 0.6)',
                                      'rgba(255, 99, 132, 0.6)',
                                      'rgba(201, 203, 207, 0.6)',
                                    ],
                                    borderWidth: 1,
                                  },
                                ],
                              }}
                              options={{
                                responsive: true,
                                plugins: {
                                  legend: {
                                    position: 'bottom' as const,
                                  },
                                },
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className='col-md-4'>
                        <div className='card'>
                          <div className='card-header'>
                            <h4 className='card-title'>Status das Reservas</h4>
                          </div>
                          <div className='card-body'>
                            <p className='text-muted'>Dados não disponíveis</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Gráficos - Linha 2 */}
                  <div className='row g-5 mb-8'>
                    {metrics.bookingsByHour && metrics.bookingsByHour.length > 0 ? (
                      <div className='col-md-6'>
                        <div className='card'>
                          <div className='card-header'>
                            <h4 className='card-title'>Reservas por Horário</h4>
                          </div>
                          <div className='card-body'>
                            <Bar
                              data={{
                                labels: metrics.bookingsByHour.map((h) => h.hour),
                                datasets: [
                                  {
                                    label: 'Reservas',
                                    data: metrics.bookingsByHour.map((h) => h.count),
                                    backgroundColor: 'rgba(54, 162, 235, 0.6)',
                                    borderColor: 'rgba(54, 162, 235, 1)',
                                    borderWidth: 1,
                                  },
                                ],
                              }}
                              options={{
                                responsive: true,
                                plugins: {
                                  legend: {
                                    position: 'top' as const,
                                  },
                                },
                                scales: {
                                  y: {
                                    beginAtZero: true,
                                  },
                                },
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className='col-md-6'>
                        <div className='card'>
                          <div className='card-header'>
                            <h4 className='card-title'>Reservas por Horário</h4>
                          </div>
                          <div className='card-body'>
                            <p className='text-muted'>Dados não disponíveis</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {metrics.bookingsByEnvironment && metrics.bookingsByEnvironment.length > 0 ? (
                      <div className='col-md-6'>
                        <div className='card'>
                          <div className='card-header'>
                            <h4 className='card-title'>Distribuição por Ambiente</h4>
                          </div>
                          <div className='card-body'>
                            <Pie
                              data={{
                                labels: metrics.bookingsByEnvironment.map((e) => e.environment),
                                datasets: [
                                  {
                                    label: 'Reservas',
                                    data: metrics.bookingsByEnvironment.map((e) => e.count),
                                    backgroundColor: [
                                      'rgba(255, 99, 132, 0.6)',
                                      'rgba(54, 162, 235, 0.6)',
                                      'rgba(255, 206, 86, 0.6)',
                                      'rgba(75, 192, 192, 0.6)',
                                      'rgba(153, 102, 255, 0.6)',
                                    ],
                                    borderWidth: 1,
                                  },
                                ],
                              }}
                              options={{
                                responsive: true,
                                plugins: {
                                  legend: {
                                    position: 'right' as const,
                                  },
                                },
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className='col-md-6'>
                        <div className='card'>
                          <div className='card-header'>
                            <h4 className='card-title'>Distribuição por Ambiente</h4>
                          </div>
                          <div className='card-body'>
                            <p className='text-muted'>Dados não disponíveis</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Tabelas de Rankings */}
                  <div className='row g-5 mb-8'>
                    {metrics.topCustomers && metrics.topCustomers.length > 0 ? (
                      <div className='col-md-6'>
                        <div className='card'>
                          <div className='card-header'>
                            <h4 className='card-title'>Top Clientes</h4>
                          </div>
                          <div className='card-body'>
                            <div className='table-responsive'>
                              <table className='table table-row-bordered table-row-gray-300 align-middle'>
                                <thead>
                                  <tr className='fw-bold text-muted'>
                                    <th className='min-w-50px'>#</th>
                                    <th className='min-w-200px'>Cliente</th>
                                    <th className='min-w-100px text-end'>Reservas</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {metrics.topCustomers.map((customer, idx) => (
                                    <tr key={idx}>
                                      <td>
                                        <span className='badge badge-light-primary'>
                                          {idx + 1}
                                        </span>
                                      </td>
                                      <td>
                                        <span className='fw-bold'>{customer.name}</span>
                                      </td>
                                      <td className='text-end'>
                                        <span className='badge badge-primary fs-7'>
                                          {customer.bookingsCount}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className='col-md-6'>
                        <div className='card'>
                          <div className='card-header'>
                            <h4 className='card-title'>Top Clientes</h4>
                          </div>
                          <div className='card-body'>
                            <p className='text-muted'>Dados não disponíveis</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {metrics.peakBookingDays && metrics.peakBookingDays.length > 0 ? (
                      <div className='col-md-6'>
                        <div className='card'>
                          <div className='card-header'>
                            <h4 className='card-title'>Dias de Pico</h4>
                          </div>
                          <div className='card-body'>
                            <div className='table-responsive'>
                              <table className='table table-row-bordered table-row-gray-300 align-middle'>
                                <thead>
                                  <tr className='fw-bold text-muted'>
                                    <th className='min-w-150px'>Dia da Semana</th>
                                    <th className='min-w-100px text-end'>Reservas</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {metrics.peakBookingDays.map((day, idx) => (
                                    <tr key={idx}>
                                      <td>
                                        <span className='fw-bold'>{day.day}</span>
                                      </td>
                                      <td className='text-end'>
                                        <span className='badge badge-light-warning fs-7'>
                                          {day.count}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className='col-md-6'>
                        <div className='card'>
                          <div className='card-header'>
                            <h4 className='card-title'>Dias de Pico</h4>
                          </div>
                          <div className='card-body'>
                            <p className='text-muted'>Dados não disponíveis</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Card de Insights */}
                  <div className='row g-5'>
                    <div className='col-12'>
                      <div className='card bg-light-info'>
                        <div className='card-body'>
                          <h4 className='card-title mb-4'>
                            <i className='fas fa-lightbulb text-info me-2'></i>
                            Insights e Recomendações
                          </h4>
                          <div className='row'>
                            <div className='col-md-4'>
                              <div className='d-flex align-items-start mb-3'>
                                <i className='fas fa-chart-line text-success fs-3 me-3'></i>
                                <div>
                                  <h6 className='fw-bold mb-1'>Crescimento</h6>
                                  <p className='text-muted mb-0 fs-7'>
                                    {metrics.bookingTrends && metrics.bookingTrends.length > 0 &&
                                    metrics.bookingTrends[0].growth > 0
                                      ? `Aumento de ${metrics.bookingTrends[0].growth.toFixed(
                                          1
                                        )}% em relação ao período anterior`
                                      : 'Analise tendências para identificar padrões'}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className='col-md-4'>
                              <div className='d-flex align-items-start mb-3'>
                                <i className='fas fa-clock text-warning fs-3 me-3'></i>
                                <div>
                                  <h6 className='fw-bold mb-1'>Horários Populares</h6>
                                  <p className='text-muted mb-0 fs-7'>
                                    {metrics.mostRequestedHours && metrics.mostRequestedHours.length > 0
                                      ? `${metrics.mostRequestedHours[0].hour} é o horário mais requisitado`
                                      : 'Identifique picos de demanda'}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className='col-md-4'>
                              <div className='d-flex align-items-start mb-3'>
                                <i className='fas fa-star text-primary fs-3 me-3'></i>
                                <div>
                                  <h6 className='fw-bold mb-1'>Oportunidades</h6>
                                  <p className='text-muted mb-0 fs-7'>
                                    {metrics.noShowRate > 10
                                      ? 'Alta taxa de no-show. Considere implementar confirmação prévia'
                                      : 'Continue monitorando indicadores de qualidade'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className='alert alert-warning d-flex align-items-center'>
                  <i className='fas fa-exclamation-triangle me-3 fs-2'></i>
                  <div>
                    <h5 className='mb-1'>Nenhum dado disponível</h5>
                    <p className='mb-0'>
                      Não há dados de reservas para o período selecionado. Tente selecionar outro
                      período ou verifique se há reservas cadastradas no sistema.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const BookingManagementDashboard: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: true},
          {
            title: 'Dashboard Reservas',
            path: 'booking/booking-management-dashboard',
            isActive: true,
          },
        ]}
      >
        Dashboard - Gestão de Reservas
      </PageTitle>
      <BookingManagementDashboardContent />
    </>
  )
}

export {BookingManagementDashboard}
